import AdminPushNotifications from './AdminPushNotifications';
import React, {
  useEffect,
  useState,
} from 'react';

import {
  FiAlertTriangle,
  FiArrowLeft,
  FiCheckCircle,
  FiChevronRight,
  FiLogOut,
  FiRefreshCw,
  FiSearch,
  FiShield,
} from 'react-icons/fi';

import {
  AdminAuthProvider,
  useAdminAuth,
} from '../../contexts/AdminAuthContext';
import { adminDb } from '../../services/adminService';
import AdminMvpPanels from './AdminMvpPanels';
import AdminAuditSearch from './AdminAuditSearch';

const formatDate = (value) => {
  if (!value) return '—';

  return new Intl.DateTimeFormat(
    'en-US',
    {
      dateStyle: 'medium',
      timeStyle: 'short',
    }
  ).format(new Date(value));
};

const titleCase = (value) =>
  String(value || 'Unknown')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );

const StatusBadge = ({
  children,
  tone = 'slate',
}) => {
  const tones = {
    green:
      'bg-emerald-100 text-emerald-800 ring-emerald-200',
    amber:
      'bg-amber-100 text-amber-800 ring-amber-200',
    red:
      'bg-red-100 text-red-800 ring-red-200',
    blue:
      'bg-blue-100 text-blue-800 ring-blue-200',
    slate:
      'bg-slate-100 text-slate-700 ring-slate-200',
  };

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

const AuthCard = ({
  title,
  description,
  children,
}) => (
  <main className="min-h-screen bg-slate-950 px-4 py-12 text-slate-900">
    <div className="mx-auto w-full max-w-md">
      <div className="mb-6 flex items-center justify-center gap-3 text-white">
        <span className="rounded-xl bg-blue-600 p-3">
          <FiShield className="h-6 w-6" />
        </span>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-300">
            MyPiggy
          </p>
          <h1 className="text-xl font-semibold">
            Administration
          </h1>
        </div>
      </div>

      <section className="rounded-2xl bg-white p-6 shadow-2xl">
        <h2 className="text-xl font-semibold">
          {title}
        </h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          {description}
        </p>
        <div className="mt-6">
          {children}
        </div>
      </section>
    </div>
  </main>
);

const ErrorMessage = ({ message }) =>
  message ? (
    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
      {message}
    </div>
  ) : null;

const AdminLogin = () => {
  const { error, signIn } = useAdminAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] =
    useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      await signIn(email, password);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCard
      title="Administrator sign in"
      description="Use your dedicated MyPiggy administrator account. Parent and Kid accounts cannot access this portal."
    >
      <ErrorMessage message={error} />

      <form
        className="space-y-4"
        onSubmit={handleSubmit}
      >
        <label className="block text-sm font-medium text-slate-700">
          Email address
          <input
            className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2.5"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) =>
              setEmail(event.target.value)
            }
          />
        </label>

        <label className="block text-sm font-medium text-slate-700">
          Password
          <input
            className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2.5"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) =>
              setPassword(event.target.value)
            }
          />
        </label>

        <button
          className="w-full rounded-lg bg-blue-700 px-4 py-2.5 font-semibold text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
          disabled={submitting}
          type="submit"
        >
          {submitting
            ? 'Signing in…'
            : 'Continue'}
        </button>
      </form>
    </AuthCard>
  );
};

const MfaCodeForm = ({
  title,
  description,
  submitLabel,
  onSubmit,
  children,
}) => {
  const { error, signOut } = useAdminAuth();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] =
    useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const success = await onSubmit(code);
      if (!success) setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCard
      title={title}
      description={description}
    >
      <ErrorMessage message={error} />
      {children}

      <form
        className="mt-5 space-y-4"
        onSubmit={handleSubmit}
      >
        <label className="block text-sm font-medium text-slate-700">
          Six-digit code
          <input
            className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-3 text-center text-xl tracking-[0.35em]"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            value={code}
            onChange={(event) =>
              setCode(
                event.target.value
                  .replace(/\D/g, '')
                  .slice(0, 6)
              )
            }
          />
        </label>

        <button
          className="w-full rounded-lg bg-blue-700 px-4 py-2.5 font-semibold text-white hover:bg-blue-600 disabled:opacity-60"
          disabled={submitting}
          type="submit"
        >
          {submitting
            ? 'Verifying…'
            : submitLabel}
        </button>

        <button
          className="w-full px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          type="button"
          onClick={signOut}
        >
          Cancel and sign out
        </button>
      </form>
    </AuthCard>
  );
};

const MfaEnrollmentRequired = () => {
  const {
    error,
    beginEnrollment,
    signOut,
  } = useAdminAuth();

  return (
    <AuthCard
      title="Secure this administrator account"
      description="MyPiggy Administration requires an authenticator app before any family information can be viewed."
    >
      <ErrorMessage message={error} />
      <div className="rounded-xl bg-blue-50 p-4 text-sm leading-6 text-blue-900">
        Use Google Authenticator, Microsoft Authenticator, Authy, 1Password, or another TOTP-compatible app.
      </div>
      <button
        className="mt-5 w-full rounded-lg bg-blue-700 px-4 py-2.5 font-semibold text-white hover:bg-blue-600"
        onClick={beginEnrollment}
      >
        Set up authenticator
      </button>
      <button
        className="mt-2 w-full px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
        onClick={signOut}
      >
        Cancel and sign out
      </button>
    </AuthCard>
  );
};

const MfaEnrollment = () => {
  const {
    enrollment,
    verifyEnrollment,
  } = useAdminAuth();

  return (
    <MfaCodeForm
      title="Scan the security code"
      description="Scan this QR code with your authenticator app, then enter the current six-digit code."
      submitLabel="Enable MFA"
      onSubmit={verifyEnrollment}
    >
      <div className="flex justify-center rounded-xl border border-slate-200 bg-white p-4">
        <img
          className="h-52 w-52"
          src={enrollment?.qr_code}
          alt="Authenticator enrollment QR code"
        />
      </div>
      {enrollment?.secret && (
        <details className="mt-3 rounded-lg bg-slate-50 p-3 text-sm">
          <summary className="cursor-pointer font-medium text-slate-700">
            Enter a setup key instead
          </summary>
          <code className="mt-2 block break-all rounded bg-white p-2 text-xs">
            {enrollment.secret}
          </code>
        </details>
      )}
    </MfaCodeForm>
  );
};

const SummaryCard = ({
  label,
  value,
  detail,
}) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
      {label}
    </p>
    <p className="mt-2 text-3xl font-bold text-slate-900">
      {value ?? '—'}
    </p>
    <p className="mt-1 text-sm text-slate-500">
      {detail}
    </p>
  </div>
);

const FamilyDetail = ({
  overview,
  onBack,
  onChanged,
}) => {
  const family = overview.family;
  const stored = overview.entitlement?.stored;
  const resolved = overview.entitlement?.resolved;

  return (
    <div>
      <button
        className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-900"
        onClick={onBack}
      >
        <FiArrowLeft /> Families
      </button>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">
            {family.display_name ||
              family.kid_family_code ||
              'Unnamed Family'}
          </h2>
          <p className="mt-1 font-mono text-xs text-slate-500">
            {family.family_id}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusBadge
            tone={family.status === 'active'
              ? 'green'
              : 'red'}
          >
            {titleCase(family.status)}
          </StatusBadge>
          <StatusBadge tone="blue">
            {titleCase(stored?.plan)}
          </StatusBadge>
          <StatusBadge
            tone={resolved?.can_write
              ? 'green'
              : 'amber'}
          >
            {titleCase(
              resolved?.effective_status
            )}
          </StatusBadge>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-semibold">Family</h3>
          <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div><dt className="text-slate-500">Family code</dt><dd className="mt-1 font-medium">{family.kid_family_code || '—'}</dd></div>
            <div><dt className="text-slate-500">Currency</dt><dd className="mt-1 font-medium">{family.currency}</dd></div>
            <div><dt className="text-slate-500">Children</dt><dd className="mt-1 font-medium">{family.active_child_count} active / {family.total_child_count} registered</dd></div>
            <div><dt className="text-slate-500">Created</dt><dd className="mt-1 font-medium">{formatDate(family.created_at)}</dd></div>
            <div className="col-span-2"><dt className="text-slate-500">Deletion scheduled</dt><dd className="mt-1 font-medium">{formatDate(family.deletion_scheduled_for)}</dd></div>
          </dl>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-semibold">Subscription & entitlement</h3>
          <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div><dt className="text-slate-500">Stored status</dt><dd className="mt-1 font-medium">{titleCase(stored?.status)}</dd></div>
            <div><dt className="text-slate-500">Resolved access</dt><dd className="mt-1 font-medium">{titleCase(resolved?.effective_status)}</dd></div>
            <div><dt className="text-slate-500">Can read</dt><dd className="mt-1 font-medium">{resolved?.can_read ? 'Yes' : 'No'}</dd></div>
            <div><dt className="text-slate-500">Can write</dt><dd className="mt-1 font-medium">{resolved?.can_write ? 'Yes' : 'No'}</dd></div>
            <div><dt className="text-slate-500">Access ends</dt><dd className="mt-1 font-medium">{formatDate(resolved?.access_ends_at)}</dd></div>
            <div><dt className="text-slate-500">Provider</dt><dd className="mt-1 font-medium">{titleCase(stored?.billing_provider || 'Not connected')}</dd></div>
          </dl>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-semibold">Adults</h3>
          <div className="mt-4 space-y-3">
            {overview.adults.map((adult) => (
              <div key={adult.parent_id} className="rounded-lg bg-slate-50 p-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold">{adult.name}</span>
                  <StatusBadge tone={adult.role === 'owner' ? 'blue' : 'slate'}>{titleCase(adult.role)}</StatusBadge>
                </div>
                <p className="mt-1 text-slate-600">{adult.email}</p>
                <p className="mt-1 text-xs text-slate-500">Verified email: {overview.adults_verified?.find((entry) => entry.parent_id === adult.parent_id)?.verified_email || 'Not verified'} · Auth linked: {adult.auth_identity_linked ? 'Yes' : 'No'}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-semibold">Children</h3>
          <div className="mt-4 space-y-3">
            {overview.children.length === 0 && (
              <p className="text-sm text-slate-500">No children registered.</p>
            )}
            {overview.children.map((child) => (
              <div key={child.child_id} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 p-3 text-sm">
                <div>
                  <p className="font-semibold">{child.first_name} {child.last_name}</p>
                  <p className="mt-1 text-xs text-slate-500">{child.user_id} · {titleCase(child.age_group)}</p>
                   <p className="mt-1 text-xs text-slate-500">Created {formatDate(child.created_at)} · Deactivated {formatDate(child.deactivated_at)}</p>
                </div>
                <StatusBadge tone={child.status === 'active' ? 'green' : 'slate'}>{titleCase(child.status)}</StatusBadge>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-semibold">Diagnostics</h3>
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {overview.diagnostics.map((item) => {
            const good = item.status === 'ok';
            return (
              <div key={item.code} className={`flex gap-3 rounded-lg border p-3 ${good ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                {good ? <FiCheckCircle className="mt-0.5 shrink-0 text-emerald-700" /> : <FiAlertTriangle className="mt-0.5 shrink-0 text-amber-700" />}
                <div><p className="text-sm font-semibold">{item.label}</p><p className="mt-1 text-xs text-slate-600">{item.detail}</p></div>
              </div>
            );
          })}
        </div>
      </section>
      <div className="mt-6"><AdminMvpPanels overview={overview} onChanged={onChanged} /></div>
    </div>
  );
};

const AdminDashboard = () => {
  const { adminContext, signOut } =
    useAdminAuth();
  const [summary, setSummary] =
    useState(null);
  const [families, setFamilies] =
    useState([]);
  const [stripeIssues, setStripeIssues] = useState([]);
  const [stripeHealth, setStripeHealth] = useState(null);
  const [search, setSearch] =
    useState('');
  const [familyStatus, setFamilyStatus] = useState('');
  const [entitlementStatus, setEntitlementStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] =
    useState(null);
  const [loading, setLoading] =
    useState(true);
  const [error, setError] =
    useState('');

  const loadDashboard = async (
    searchValue = '', nextOffset = 0
  ) => {
    setLoading(true);
    setError('');

    try {
      const [nextSummary, nextFamilies, nextStripeIssues, nextStripeHealth] =
        await Promise.all([
          adminDb.getDashboardSummary(),
          adminDb.listFamilies(searchValue, familyStatus || null, entitlementStatus || null, nextOffset),
          adminDb.listStripeReconciliationIssues(),
          adminDb.getStripeReconciliationHealth(),
        ]);

      setSummary(nextSummary);
      setFamilies(nextFamilies.rows);
      setStripeIssues(nextStripeIssues);
      setStripeHealth(nextStripeHealth);
      setTotal(nextFamilies.total);
      setOffset(nextOffset);
    } catch {
      setError(
        'The administration data could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const openFamily = async (familyId) => {
    setLoading(true);
    setError('');

    try {
      setSelected(
        await adminDb.getFamilyOverview(
          familyId
        )
      );
    } catch {
      setError(
        'The family overview could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <header className="border-b border-slate-800 bg-slate-950 text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <span className="rounded-lg bg-blue-600 p-2"><FiShield /></span>
            <div><p className="text-xs uppercase tracking-wider text-blue-300">MyPiggy</p><h1 className="font-semibold">Administration</h1></div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="hidden text-slate-300 sm:inline">{adminContext.display_name}</span>
            <button className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 hover:bg-slate-800" onClick={signOut}><FiLogOut /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <ErrorMessage message={error} />
        <AdminPushNotifications />

        {selected ? (
          <FamilyDetail
            overview={selected}
            onBack={() => setSelected(null)}
            onChanged={() => openFamily(selected.family.family_id)}
          />
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <SummaryCard label="Registered families" value={summary?.registered_families} detail={`${summary?.active_families ?? '—'} active · ${summary?.inactive_families ?? '—'} inactive`} />
              <SummaryCard label="Family owners" value={summary?.family_owners} detail="Owner memberships" />
              <SummaryCard label="Guardians" value={summary?.guardians} detail="Guardian memberships" />
              <SummaryCard label="Kids" value={summary?.registered_kids} detail={`${summary?.active_kids ?? '—'} active · ${summary?.inactive_kids ?? '—'} inactive`} />
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <SummaryCard label="Paid subscriptions" value={summary?.paid_subscriptions} detail="Active or in grace period" />
              <SummaryCard label="Families on trial" value={summary?.trial_families} detail="Current trial entitlement" />
              <SummaryCard label="Family Plus subscribers" value={summary?.family_plus_subscribers} detail="Paid Family Plus plan" />
              <SummaryCard label="Family subscribers" value={summary?.family_subscribers} detail="Paid Family plan" />
            </div>

            {stripeHealth && (
              <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="font-semibold">Stripe reconciliation</h2>
                <p className="mt-2 text-sm text-slate-700">
                  {stripeHealth.current ?? 0} of {stripeHealth.linked ?? 0} linked subscriptions verified in the last 30 hours.
                  {' '}{stripeHealth.needs_attention ?? 0} need attention.
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Latest successful check: {formatDate(stripeHealth.latest_success_at)}.
                  Daily check runs at 02:50 UTC; failed checks are retried after six hours.
                </p>
              </section>
            )}
            {stripeIssues.length > 0 && (
              <section className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-5">
                <h2 className="flex items-center gap-2 font-semibold text-amber-900">
                  <FiAlertTriangle /> Stripe reconciliation needs attention ({stripeIssues.length})
                </h2>
                <p className="mt-1 text-sm text-amber-900">A check failed or has not succeeded in the last 30 hours. Access remains unchanged until Stripe can be verified.</p>
                <ul className="mt-3 space-y-2 text-sm">
                  {stripeIssues.map((issue) => (
                    <li key={issue.family_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white p-3">
                      <span>{issue.family_name || issue.family_id} · {issue.last_error || 'No recent successful check'}
                        <span className="block text-xs text-slate-500">Last success: {formatDate(issue.last_success_at)} · Retry: {formatDate(issue.next_retry_at)}</span>
                      </span>
                      <button type="button" className="font-medium text-blue-700" onClick={() => openFamily(issue.family_id)}>Open family</button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div><h2 className="text-lg font-semibold">Registered families</h2><p className="mt-1 text-sm text-slate-500">Search by Owner, family name, family code, or UUID.</p></div>
                  <button className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100" onClick={() => loadDashboard(search, offset)}><FiRefreshCw /> Refresh</button>
                </div>
                <form className="mt-4 flex flex-wrap gap-2" onSubmit={(event) => { event.preventDefault(); loadDashboard(search); }}>
                  <div className="relative flex-1"><FiSearch className="absolute left-3 top-3 text-slate-400" /><input className="w-full rounded-lg border border-slate-300 py-2.5 pl-10 pr-3" placeholder="Search families…" value={search} onChange={(event) => setSearch(event.target.value)} /></div>
                  <select aria-label="Family status" className="rounded-lg border p-2" value={familyStatus} onChange={(event) => setFamilyStatus(event.target.value)}>
                    <option value="">All family statuses</option><option value="active">Active</option><option value="admin_read_only">Admin read-only</option><option value="inactive">Pending deletion</option>
                  </select>
                  <select aria-label="Entitlement status" className="rounded-lg border p-2" value={entitlementStatus} onChange={(event) => setEntitlementStatus(event.target.value)}>
                    <option value="">All entitlements</option>{['trial','active','grace','expired'].map((value) => <option key={value} value={value}>{titleCase(value)}</option>)}
                  </select>
                  <button className="rounded-lg bg-blue-700 px-4 py-2.5 font-semibold text-white hover:bg-blue-600" type="submit">Search</button>
                </form>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-5 py-3">Family / Code</th><th className="px-5 py-3">Owner</th><th className="px-5 py-3">Plan</th><th className="px-5 py-3">Access</th><th className="px-5 py-3">Details</th><th className="px-5 py-3">Lifecycle</th><th className="px-5 py-3"><span className="sr-only">Open</span></th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {!loading && families.length === 0 && <tr><td className="px-5 py-10 text-center text-slate-500" colSpan={7}>No families matched this search.</td></tr>}
                    {families.map((family) => (
                      <tr key={family.id} className="hover:bg-slate-50">
                        <td className="px-5 py-4"><p className="font-semibold">{family.display_name || 'Unnamed Family'}</p><p className="mt-1 font-mono text-xs text-slate-500">{family.kid_family_code || family.id}</p></td>
                        <td className="px-5 py-4"><p>{family.owner_name || 'Missing Owner'}</p><p className="mt-1 text-xs text-slate-500">{family.owner_email || '—'}</p></td>
                        <td className="px-5 py-4"><StatusBadge tone="blue">{titleCase(family.plan)}</StatusBadge></td>
                        <td className="px-5 py-4"><StatusBadge tone={family.entitlement_status === 'active' ? 'green' : 'amber'}>{titleCase(family.entitlement_status)}</StatusBadge></td>
                        <td className="px-5 py-4">—</td>
                        <td className="px-5 py-4"><StatusBadge tone={family.family_status === 'active' ? 'green' : 'red'}>{titleCase(family.family_status)}</StatusBadge></td>
                        <td className="px-5 py-4"><button className="rounded-lg p-2 text-blue-700 hover:bg-blue-50" aria-label={`Open ${family.display_name || 'family'}`} onClick={() => openFamily(family.id)}><FiChevronRight /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {loading && <div className="flex items-center justify-center gap-2 border-t border-slate-200 px-5 py-6 text-sm text-slate-500"><FiRefreshCw className="animate-spin" /> Loading…</div>}
              <div className="flex items-center justify-between border-t p-4 text-sm"><span>{total} matching families</span><div className="flex gap-3"><button disabled={loading || offset === 0} onClick={() => loadDashboard(search, Math.max(0, offset - 25))}>Previous</button><button disabled={loading || offset + 25 >= total} onClick={() => loadDashboard(search, offset + 25)}>Next</button></div></div>
            </section>
            <AdminAuditSearch />
          </>
        )}
      </main>
    </div>
  );
};

const AdminPortalContent = () => {
  const {
    phase,
    error,
    signOut,
    retry,
    verifyChallenge,
  } = useAdminAuth();

  useEffect(() => {
    const previousTitle = document.title;
    const existing = document.querySelector(
      'meta[name="robots"]'
    );
    const previousContent =
      existing?.getAttribute('content');
    const meta = existing ||
      document.createElement('meta');

    meta.setAttribute('name', 'robots');
    meta.setAttribute(
      'content',
      'noindex, nofollow, noarchive'
    );

    if (!existing) {
      document.head.appendChild(meta);
    }

    document.title =
      'MyPiggy Administration';

    return () => {
      document.title = previousTitle;

      if (existing) {
        if (previousContent === null) {
          existing.removeAttribute('content');
        } else {
          existing.setAttribute(
            'content',
            previousContent
          );
        }
      } else {
        meta.remove();
      }
    };
  }, []);

  if (phase === 'loading') {
    return (
      <AuthCard
        title="Checking secure access"
        description="Verifying your administrator session and MFA status."
      >
        <div className="flex items-center justify-center gap-3 py-6 text-slate-600">
          <FiRefreshCw className="animate-spin" />
          Please wait…
        </div>
      </AuthCard>
    );
  }

  if (phase === 'signed_out') {
    return <AdminLogin />;
  }

  if (phase === 'enrollment_required') {
    return <MfaEnrollmentRequired />;
  }

  if (phase === 'enrollment') {
    return <MfaEnrollment />;
  }

  if (phase === 'challenge') {
    return (
      <MfaCodeForm
        title="Verify your identity"
        description="Enter the current code from your authenticator app to continue."
        submitLabel="Verify and continue"
        onSubmit={verifyChallenge}
      />
    );
  }

  if (phase === 'denied') {
    return (
      <AuthCard
        title="Access denied"
        description="This authenticated account is not an active MyPiggy administrator."
      >
        <div className="flex items-start gap-3 rounded-xl bg-red-50 p-4 text-sm text-red-800">
          <FiAlertTriangle className="mt-0.5 shrink-0" />
          No family information was disclosed.
        </div>
        <button className="mt-5 w-full rounded-lg bg-slate-900 px-4 py-2.5 font-semibold text-white" onClick={signOut}>Sign out</button>
      </AuthCard>
    );
  }

  if (phase === 'error') {
    return (
      <AuthCard
        title="Unable to verify access"
        description="The administrator session could not be checked."
      >
        <ErrorMessage message={error} />
        <button className="w-full rounded-lg bg-blue-700 px-4 py-2.5 font-semibold text-white" onClick={retry}>Try again</button>
        <button className="mt-2 w-full px-4 py-2 text-sm font-medium text-slate-600" onClick={signOut}>Sign out</button>
      </AuthCard>
    );
  }

  return <AdminDashboard />;
};

const AdminPortal = () => (
  <AdminAuthProvider>
    <AdminPortalContent />
  </AdminAuthProvider>
);

export default AdminPortal;

